import { Observable, BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  FormQuestionsService,
  QuestionBase,
  DropdownQuestion,
  CheckboxQuestion,
  QuestionGroup,
  WysiwygQuestion,
  ProfileService
} from 'core/index';

@Injectable({
  providedIn: 'root'
})
export class DefaultCoursesQuestionsService implements FormQuestionsService {
  protected questionsSubject = new BehaviorSubject(null);
  public form: UntypedFormGroup;
  public formData: any;

  constructor(protected translate: TranslateService, protected profileService: ProfileService) {}

  getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  getFormData() {
    return null;
  }

  getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  createQuestions(params: any = {}) {
    const questions = [...this.createSpecificQuestions(params)]
      .filter((question) => question != null)
      .sort((a, b) => a.order - b.order);
    this.questionsSubject.next(questions);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  createSpecificQuestions(params: any = {}): QuestionBase<any>[] {
    return [];
  }

  // Questions common to all roles
  public getDiplomaQuestion(params: any = {}): QuestionBase<any> {
    const { course, disabledQuestion } = params;

    return new CheckboxQuestion({
      key: 'shouldSendDiploma',
      label: 'courses.config.diploma',
      class: 'col-1',
      disabled: disabledQuestion || course.configuration?.isDiplomaDisabled,
      value: course.configuration?.shouldSendDiploma ?? false,
      order: 6
    });
  }

  // Questions common to some roles
  public getAccessModeQuestion(params: any = {}): QuestionBase<any> {
    const { course, defaultAccessMode, disabledQuestion } = params;

    return defaultAccessMode
      ? new DropdownQuestion({
          class: 'col-1',
          key: 'accessMode',
          label: 'courses.config.accessMode',
          value: course.accessMode || 'default',
          disabled: disabledQuestion,
          options: [
            {
              key: 'default',
              value: this.translate.instant('courses.config.accessModes.default', {
                mode: this.translate.instant(`courses.config.accessModes.${defaultAccessMode}`)
              })
            },
            {
              key: 'open',
              value: this.translate.instant('courses.config.accessModes.open'),
              disabled: course.disableOpenAccess
            },
            { key: 'limited', value: this.translate.instant('courses.config.accessModes.limited') },
            { key: 'unavailable', value: this.translate.instant('courses.config.accessModes.unavailable') }
          ],
          order: 4
        })
      : null;
  }

  /**
   * Get the data of the meta courses
   * @param params
   * @returns Question Group object
   */
  public getCourseMeta(params: any = {}): QuestionGroup {
    const { course } = params;
    const isEditable = this.profileService.canExecute('courses.meta.edit') || false;

    const courseMetaGroup: QuestionGroup = new QuestionGroup({
      key: 'courseMetaIds',
      title: 'courses.info',
      isSection: true,
      isDropdown: true,
      isOpenCollapsible: false,
      childQuestions: [
        new WysiwygQuestion({
          key: 'objectives',
          label: 'courses.meta.objectives',
          class: 'col-1',
          heightBox: 200,
          value: course.objectives,
          editable: isEditable,
          order: 1
        }),
        new WysiwygQuestion({
          key: 'contents',
          label: 'courses.meta.contents',
          class: 'col-1',
          heightBox: 200,
          value: course.contents,
          editable: isEditable,
          order: 2
        }),
        new WysiwygQuestion({
          key: 'aimedAt',
          label: 'courses.meta.aimedAt',
          class: 'col-1',
          heightBox: 200,
          value: course.aimedAt,
          editable: isEditable,
          order: 3
        })
      ]
        .filter((question) => question != null)
        .sort((a, b) => a.order - b.order)
    });
    return courseMetaGroup;
  }
}
